'use client'

import { useState } from 'react'

import { useLocalStorage } from 'react-use'

import { FeatureCard } from '~/components/feature-cards/FeatureCard'
import {
  FeatureCardSelection,
  pickFeatureCard,
  SPLITS_CONNECT_CARD_ID,
} from '~/components/feature-cards/pickFeatureCard'
import { FeatureCardTracking } from '~/components/feature-cards/tracking'
import { getSplitsConnectInstallAttribution } from '~/constants/externalUrls'
import { DISMISSED_FEATURE_CARDS_KEY } from '~/constants/localStorage'
import { useActiveOrgId } from '~/hooks/org'
import { useCanInstallSplitsConnect } from '~/hooks/useCanInstallSplitsConnect'
import { useFeatureCards } from '~/hooks/useFeatureCards'
import { useIsGettingStarted } from '~/hooks/useIsGettingStarted'
import { sendTrackingData } from '~/utils/tracking'

/**
 * Renders at most one feature card per page load: the latest unread published
 * card, else the Splits Connect promo when eligible. The choice is latched
 * when the cards query settles — dismissing a card hides it without promoting
 * the next candidate until a future page load.
 */
export const FeatureCardSlot = () => {
  const { featureCards, isSettled } = useFeatureCards()
  const [dismissedIds = [], setDismissedIds] = useLocalStorage<string[]>(
    DISMISSED_FEATURE_CARDS_KEY,
    [],
  )
  const canInstallSplitsConnect = useCanInstallSplitsConnect()
  const orgId = useActiveOrgId()

  // Latch once the query settles so a refetch or the dismissal write can't
  // re-pick. Set during render, not an effect.
  const [selection, setSelection] = useState<FeatureCardSelection | 'pending'>(
    'pending',
  )
  if (isSettled && selection === 'pending') {
    setSelection(
      pickFeatureCard({
        latestCard: featureCards[0],
        dismissedIds,
        canInstallSplitsConnect,
      }),
    )
  }

  const dismissCard = (id: string) => {
    setDismissedIds([...dismissedIds, id])
  }

  if (selection === 'pending' || selection === null) {
    return null
  }

  if (selection.type === 'published') {
    if (dismissedIds.includes(selection.card.id)) {
      return null
    }

    return (
      <FeatureCard
        showNewLabel
        title={selection.card.title}
        description={selection.card.description}
        ctaText="Learn more"
        ctaHref={selection.card.learnMoreUrl}
        imageUrl={selection.card.imageUrl}
        darkImageUrl={selection.card.darkImageUrl}
        onDismiss={() => dismissCard(selection.card.id)}
        tracking={{
          cardId: selection.card.id,
          cardName: selection.card.title,
          cardType: 'published',
          orgId,
        }}
      />
    )
  }

  // Catches an extension announcement that lands after the pick latched
  if (
    dismissedIds.includes(SPLITS_CONNECT_CARD_ID) ||
    !canInstallSplitsConnect
  ) {
    return null
  }

  return (
    <SplitsConnectCard
      onDismiss={() => dismissCard(SPLITS_CONNECT_CARD_ID)}
      tracking={{
        cardId: SPLITS_CONNECT_CARD_ID,
        cardName: 'Splits Connect',
        cardType: 'splitsConnect',
        orgId,
      }}
    />
  )
}

/**
 * Mounted only when the promo is the live candidate, so the getting-started
 * queries don't fire for users who'd never see it.
 */
const SplitsConnectCard = ({
  onDismiss,
  tracking,
}: {
  onDismiss: () => void
  tracking: FeatureCardTracking
}) => {
  const { isGettingStarted, isSettled } = useIsGettingStarted()
  const installAttribution = getSplitsConnectInstallAttribution('featureCard')

  // The Welcome screen already promotes the extension — stay quiet until the
  // org has activity.
  if (!isSettled || isGettingStarted) {
    return null
  }

  return (
    <FeatureCard
      title="Splits Connect"
      description="Transact in external apps using your Splits accounts"
      ctaText="Get the extension"
      ctaHref={installAttribution.url}
      onCtaClick={() =>
        sendTrackingData(
          'clickedInstallSplitsConnect',
          installAttribution.eventProperties,
        )
      }
      onDismiss={onDismiss}
      tracking={tracking}
    />
  )
}
